import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Input } from '@design-system/components/input/input';
import { Typography } from '@design-system/components/typography/typography';
import { hintItem, wrapper, hintsContainer } from './titleInputWithHints.css';
import { isEmpty } from 'lodash';
import { ExerciseSuggestion } from '@client/new/pages/creator/library/models/exercise-suggestions.model';

type Hint = { title: string; description?: string; uuid: string };

type TitleInputWithHintsProps = {
  required?: boolean;
  value?: string;
  onChange: (value: string) => void;
  onSearchChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: string;
  exerciseSuggestions: ExerciseSuggestion[];
  onUpdate: (description: string, uuid: string) => void;
};

export const TitleInputWithHints = ({
  required,
  value,
  onChange,
  onSearchChange,
  onBlur = () => null,
  disabled,
  error,
  exerciseSuggestions = [],
  onUpdate,
}: TitleInputWithHintsProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [inputFocus, setInputFocus] = useState(false);

  const handleClick = ({ title, description, uuid }: Hint) => {
    onChange(title);
    onSearchChange?.(title);
    if (description !== undefined) {
      onUpdate(description, uuid);
    }
  };

  const handleBlur = useCallback(() => {
    setInputFocus(false);
    onBlur();
  }, [onBlur]);

  const handleOutsideClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (
        wrapperRef.current &&
        target &&
        !wrapperRef.current.contains(target)
      ) {
        handleBlur();
      }
    },
    [handleBlur],
  );

  useEffect(() => {
    if (!inputFocus) return;

    document.addEventListener('click', handleOutsideClick, true);

    return () => {
      document.removeEventListener('click', handleOutsideClick, true);
    };
  }, [handleOutsideClick, inputFocus]);

  const onInputChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const nextValue = ev.target.value;
    onChange(nextValue);
    onSearchChange?.(nextValue);
  };

  return (
    <div className={wrapper} ref={wrapperRef}>
      <Input
        label="Title"
        required={required}
        value={value}
        onChange={onInputChange}
        disabled={disabled}
        error={error}
        autoComplete="off"
        onFocus={() => setInputFocus(true)}
        onBlur={handleBlur}
      />
      {inputFocus && !isEmpty(exerciseSuggestions) ? (
        <div className={hintsContainer}>
          {exerciseSuggestions.map((option) => (
            <div key={option.id} className={hintItem}>
              <Typography
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleClick({
                    uuid: option.id,
                    title: option.name,
                    description: option.description,
                  });
                }}
              >
                {option.name}
              </Typography>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
